<div class="w-full">
  <p-card>
    <ng-template pTemplate="title">
      <div class="flex justify-between items-center w-full">
        <div class="flex gap-2">
          <span class="text-2xl font-bold">
            Contract # {{ contract?.contractNo ?? contractId }}
          </span>
        </div>
        <div class="flex gap-2"></div>
        <div class="flex gap-2">
          <p-button
            label="Back to List"
            icon="pi pi-arrow-left"
            styleClass="p-button-text"
            (onClick)="goBackToList()"
          ></p-button>
          <ng-container *ngIf="!isEditMode">
            <p-button
              *ngIf="
                contract?.contractStatus?.statusName === 'Draft' ||
                contract?.contractStatus?.statusName === 'Pending - Sales'
              "
              label="Edit Contract"
              icon="pi pi-pencil"
              styleClass="p-button-outlined p-button-secondary"
              (onClick)="editContract()"
            ></p-button>
          </ng-container>
          <ng-container *ngIf="isEditMode">
            <p-button
              label="Cancel"
              icon="pi pi-times"
              styleClass="p-button-outlined p-button-secondary"
              (onClick)="cancelEdit()"
            ></p-button>
            <p-button
              label="Save"
              icon="pi pi-save"
              styleClass="p-button-success"
              [loading]="isSaving"
              (onClick)="saveContract()"
            ></p-button>
          </ng-container>
        </div>
      </div>
    </ng-template>

    <div
      *ngIf="isLoading; else contractContent"
      class="flex justify-center items-center p-8"
    >
      <p-progressSpinner></p-progressSpinner>
    </div>

    <ng-template #contractContent>
      <div class="w-full mx-auto" *ngIf="contract; else noContract">
        <p-fieldset [toggleable]="true" class="mb-4">
          <ng-template pTemplate="header">
            <div
              class="flex align-items-center justify-content-between w-full mb-3 border-b-4 p-2 border-solid border-100"
            >
              <span class="font-bold mr-2">Contract Header</span>
            </div>
          </ng-template>

          <div [formGroup]="headerForm" class="grid">
            <!-- Contract Name -->
            <div
              *ngIf="contract?.contractName || isEditMode"
              class="col-12 md:col-6 lg:col-4 mb-2"
            >
              <strong>Contract Name</strong>
              <div class="mt-1">
                <span *ngIf="!isEditMode">{{ contract?.contractName }}</span>
                <input
                  *ngIf="isEditMode"
                  pInputText
                  formControlName="contractName"
                  class="w-full"
                />
              </div>
            </div>

            <!-- Contract No (read-only) -->
            <div *ngIf="contract?.contractNo" class="col-12 md:col-6 lg:col-4">
              <strong>Contract No</strong>
              <div class="mt-1">{{ contract?.contractNo }}</div>
            </div>

            <!-- Contract Type (read-only) -->
            <div
              *ngIf="contract?.contractType"
              class="col-12 md:col-6 lg:col-4"
            >
              <strong>Contract Type</strong>
              <div class="mt-1">{{ contract?.contractType }}</div>
            </div>

            <!-- Batch Number (read-only) -->
            <div
              *ngIf="contract?.batchNumber"
              class="col-12 md:col-6 lg:col-4"
            >
              <strong>Batch Number</strong>
              <div class="mt-1">{{ contract?.batchNumber }}</div>
            </div>

            <!-- Customer (read-only) -->
            <div
              *ngIf="contract?.customer?.customerName"
              class="col-12 md:col-6 lg:col-4"
            >
              <strong>Customer</strong>
              <div class="mt-1">
                {{ contract?.customer?.customerNumber }} -
                {{ contract?.customer?.customerName }}
              </div>
            </div>

            <!-- Start Date -->
            <div
              *ngIf="contract?.startDateActive || isEditMode"
              class="col-12 md:col-6 lg:col-4"
            >
              <strong>Start Date</strong>
              <div class="mt-1">
                <span *ngIf="!isEditMode">{{ contract?.startDateActive | date }}</span>
                <p-datePicker
                  *ngIf="isEditMode"
                  formControlName="startDateActive"
                  [showIcon]="true"
                  dateFormat="dd-mm-yy"
                  [appendTo]="'body'"
                  styleClass="w-full"
                ></p-datePicker>
              </div>
            </div>

            <!-- End Date -->
            <div
              *ngIf="contract?.endDateActive || isEditMode"
              class="col-12 md:col-6 lg:col-4"
            >
              <strong>End Date</strong>
              <div class="mt-1">
                <span *ngIf="!isEditMode">{{ contract?.endDateActive | date }}</span>
                <p-datePicker
                  *ngIf="isEditMode"
                  formControlName="endDateActive"
                  [showIcon]="true"
                  dateFormat="dd-mm-yy"
                  [appendTo]="'body'"
                  styleClass="w-full"
                ></p-datePicker>
              </div>
            </div>

            <!-- Created By (read-only) -->
            <div
              *ngIf="contract?.createdBy?.fullName"
              class="col-12 md:col-6 lg:col-4"
            >
              <strong>Created By</strong>
              <div class="mt-1">{{ contract?.createdBy?.fullName }}</div>
            </div>

            <!-- Created At (read-only) -->
            <div *ngIf="contract?.createdAt" class="col-12 md:col-6 lg:col-4">
              <strong>Created At</strong>
              <div class="mt-1">{{ contract?.createdAt | date }}</div>
            </div>

            <!-- Status (read-only) -->
            <div
              *ngIf="contract?.contractStatus"
              class="col-12 md:col-6 lg:col-4"
            >
              <strong>Status</strong>
              <div class="mt-1">
                {{ contract?.contractStatus?.statusName ?? "N/A" }}
              </div>
            </div>

            <!-- Contract Note -->
            <div
              *ngIf="contract?.contractNote1 || isEditMode"
              class="col-12 md:col-6 lg:col-4"
            >
              <strong>Contract Note</strong>
              <div class="mt-1">
                <span *ngIf="!isEditMode">{{ contract?.contractNote1 }}</span>
                <textarea
                  *ngIf="isEditMode"
                  pInputTextarea
                  formControlName="contractNote1"
                  rows="3"
                  class="w-full"
                ></textarea>
              </div>
            </div>
          </div>
        </p-fieldset>

        <p-fieldset [toggleable]="true" class="mb-4">
          <ng-template pTemplate="header">
            <div
              class="flex align-items-center justify-content-between w-full mb-3 border-b-4 p-2 border-solid border-100"
            >
              <span class="font-bold mr-2">Approval Workflow</span>
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <app-approval-card
              [invokedFromChat]="false"
              [businessObjectId]="contract?.contractId ?? 0"
              [businessObjectDescription]="
                contract?.customer?.customerName ?? ''
              "
              [currentUser]="currentUser"
              [approvalContext]="{
                customerId: contract?.customer?.id,
                businessId: contract?.business?.id
              }"
              [workflowApprovals]="contract?.workflowApprovals ?? []"
              (statusChange)="onStatusChange($event)"
            >
            </app-approval-card>
          </ng-template>
        </p-fieldset>

        <p-fieldset
          *ngIf="contract?.contractDetails"
          [toggleable]="true"
          class="mb-4"
        >
          <ng-template pTemplate="header">
            <div
              class="flex align-items-center justify-content-between w-full mb-3 border-b-4 p-2 border-solid border-100"
            >
              <span class="font-bold mr-2">Contract Details</span>
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <div
              class="flex justify-content-start gap-2 mb-3"
              *ngIf="isEditable && details.length"
            >
              <p-button
                *ngIf="!isDetailsEditMode"
                label="Edit Details"
                icon="pi pi-pencil"
                styleClass="p-button-outlined p-button-secondary p-button-sm"
                (onClick)="editDetails()"
              ></p-button>
              <ng-container *ngIf="isDetailsEditMode">
                <p-button
                  label="Cancel"
                  icon="pi pi-times"
                  styleClass="p-button-outlined p-button-secondary p-button-sm"
                  (onClick)="cancelEditDetails()"
                ></p-button>
                <p-button
                  label="Save Details"
                  icon="pi pi-save"
                  styleClass="p-button-success p-button-sm"
                  [loading]="isSaving"
                  (onClick)="saveContractDetails()"
                ></p-button>
              </ng-container>
            </div>
            <!-- outer wrapper that constrains to the fieldset width -->
            <div class="contract-table-scroll w-full">
              <!-- inner block that becomes as wide as the table content -->
              <div class="contract-table-inner">
                <p-table
                  *ngIf="contract.contractDetails"
                  #dt
                  [value]="details"
                  [loading]="isLoading"
                  [lazy]="true"
                  dataKey="id"
                  styleClass="contract-details-table p-datatable-striped min-w-[100%] max-w-[1200px]"
                  [resizableColumns]="true"
                >
                  <ng-template pTemplate="header">
                    <tr>
                      <th>Business Name</th>
                      <th>Business No</th>
                      <th>Vendor Name</th>
                      <th>Vendor No</th>
                      <th>Vendor Site</th>
                      <th>Item</th>
                      <th>Rebate</th>
                      <th>JBP</th>
                      <th>Fixed JBP</th>
                      <th>Slab 1</th>
                      <th>Slab 1 %</th>
                      <th>Slab 2</th>
                      <th>Slab 2 %</th>
                      <th>Slab 3</th>
                      <th>Slab 3 %</th>
                      <th>Deliverables</th>
                      <th>Calculation Method</th>

                      <th style="width: 10rem">Actions</th>
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="body" let-detail>
                    <tr>
                      <td>{{ detail.business?.businessName }}</td>
                      <td>{{ detail.business?.businessCode }}</td>
                      <td>{{ detail.vendor?.supplierName }}</td>
                      <td>{{ detail.vendor?.supplierNumber }}</td>
                      <td>{{ detail.vendorSite?.supplierSiteNumber }}</td>
                      <td>
                        {{
                          detail.item
                            ? detail?.item?.itemNumber +
                              " - " +
                              detail.item?.erpItemNameE
                            : ""
                        }}
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.rebate }}</span>
                        <p-inputNumber *ngIf="isDetailsEditMode" [(ngModel)]="detail.rebate" [minFractionDigits]="0" [maxFractionDigits]="2" styleClass="w-7rem"></p-inputNumber>
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.jbpPrc }}</span>
                        <p-inputNumber *ngIf="isDetailsEditMode" [(ngModel)]="detail.jbpPrc" [minFractionDigits]="0" [maxFractionDigits]="2" styleClass="w-7rem"></p-inputNumber>
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.fixedJbp }}</span>
                        <p-inputNumber *ngIf="isDetailsEditMode" [(ngModel)]="detail.fixedJbp" [minFractionDigits]="0" [maxFractionDigits]="2" styleClass="w-7rem"></p-inputNumber>
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.progressiveSlap1 }}</span>
                        <p-inputNumber *ngIf="isDetailsEditMode" [(ngModel)]="detail.progressiveSlap1" [minFractionDigits]="0" [maxFractionDigits]="2" styleClass="w-7rem"></p-inputNumber>
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.progressiveSlap1Prc }}</span>
                        <p-inputNumber *ngIf="isDetailsEditMode" [(ngModel)]="detail.progressiveSlap1Prc" [minFractionDigits]="0" [maxFractionDigits]="2" styleClass="w-7rem"></p-inputNumber>
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.progressiveSlap2 }}</span>
                        <p-inputNumber *ngIf="isDetailsEditMode" [(ngModel)]="detail.progressiveSlap2" [minFractionDigits]="0" [maxFractionDigits]="2" styleClass="w-7rem"></p-inputNumber>
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.progressiveSlap2Prc }}</span>
                        <p-inputNumber *ngIf="isDetailsEditMode" [(ngModel)]="detail.progressiveSlap2Prc" [minFractionDigits]="0" [maxFractionDigits]="2" styleClass="w-7rem"></p-inputNumber>
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.progressiveSlap3 }}</span>
                        <p-inputNumber *ngIf="isDetailsEditMode" [(ngModel)]="detail.progressiveSlap3" [minFractionDigits]="0" [maxFractionDigits]="2" styleClass="w-7rem"></p-inputNumber>
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.progressiveSlap3Prc }}</span>
                        <p-inputNumber *ngIf="isDetailsEditMode" [(ngModel)]="detail.progressiveSlap3Prc" [minFractionDigits]="0" [maxFractionDigits]="2" styleClass="w-7rem"></p-inputNumber>
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.deliverables }}</span>
                        <input *ngIf="isDetailsEditMode" pInputText [(ngModel)]="detail.deliverables" class="w-12rem" />
                      </td>
                      <td>
                        <span *ngIf="!isDetailsEditMode">{{ detail.calculationMethod }}</span>
                        <input *ngIf="isDetailsEditMode" pInputText [(ngModel)]="detail.calculationMethod" class="w-10rem" />
                      </td>

                      <td>
                        <!-- Row-level actions intentionally disabled; editing is handled in bulk via "Edit Details". -->
                      </td>
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="emptymessage">
                    <tr>
                      <td colspan="4">No Contract Details Found.</td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
            </div>
          </ng-template>
        </p-fieldset>

        <p-fieldset
          *ngIf="contract"
          [toggleable]="true"
          class="mb-4 w-full max-w-full"
          styleClass="contract-fieldset mb-4 w-full max-w-full"
        >
          <ng-template pTemplate="header">
            <div
              class="flex align-items-center justify-content-between w-full mb-3 border-b-4 p-2 border-solid border-100"
            >
              <span class="font-bold mr-2">Attachments</span>
            </div>
          </ng-template>

          <ng-template pTemplate="content">
            <div class="mb-4">
              <p-fileUpload
                name="attachments[]"
                (onSelect)="onFileSelect($event)"
                [multiple]="true"
                [accept]="attachmentAcceptTypes"
                [maxFileSize]="maxFileSize"
                [showUploadButton]="false"
                [showCancelButton]="false"
                chooseLabel="Add Attachment"
                chooseIcon="pi pi-plus"
                styleClass="p-button-outlined"
                [disabled]="isUploadingAttachments || isSavingAttachments"
              >
              </p-fileUpload>
            </div>
            <div class="mb-3" *ngIf="isUploadingAttachments">
              <div class="mb-2 text-600 text-sm">Uploading attachments {{ attachmentUploadProgress }}%</div>
              <p-progressBar [value]="attachmentUploadProgress"></p-progressBar>
            </div>
            <div *ngIf="attachmentsChanged" class="flex justify-content-end mt-4">
              <p-button
                label="Save Attachment Changes"
                icon="pi pi-save"
                (click)="saveAttachments()"
                [loading]="isUploadingAttachments || isSavingAttachments"
                [disabled]="isUploadingAttachments || isSavingAttachments"
              ></p-button>
            </div>
            <p-table
              [value]="contract.attachments"
              styleClass="p-datatable-sm"
              responsiveLayout="scroll"
              *ngIf="contract.attachments && contract.attachments.length > 0"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th>File Name</th>
                  <th>Size</th>
                  <th>Uploaded By</th>
                  <th>Uploaded Date</th>
                  <th style="width: 10rem">Actions</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-attachment>
                <tr>
                  <td>
                    <i class="pi pi-file mr-2"></i>
                    {{ attachment.fileName }}
                  </td>
                  <td>{{ formatFileSize(attachment.fileSize) }}</td>
                  <td>{{ attachment.uploadedBy || "N/A" }}</td>
                  <td>{{ attachment.uploadedDate | appDate : "dd-MM-yyyy" }}</td>
                  <td>
                    <div class="flex gap-2">
                      <button
                        pButton
                        pRipple
                        icon="pi pi-eye"
                        class="p-button-rounded p-button-info"
                        (click)="viewAttachment(attachment)"
                        pTooltip="View"
                        [disabled]="!attachment.attachmentId"
                      ></button>
                      <button
                        pButton
                        pRipple
                        icon="pi pi-download"
                        class="p-button-rounded p-button-success"
                        (click)="downloadAttachment(attachment)"
                        pTooltip="Download in new tab"
                      ></button>
                      <button
                        pButton
                        pRipple
                        icon="pi pi-envelope"
                        class="p-button-rounded p-button-help"
                        (click)="emailAttachment(attachment)"
                        pTooltip="Email"
                      ></button>
                    </div>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </ng-template>
          <div
            *ngIf="!contract.attachments || contract.attachments.length === 0"
            class="text-center text-600 p-3"
          >
            No attachments found.
          </div>

        </p-fieldset>
      </div>
    </ng-template>

    <ng-template #noContract>
      <div class="text-center p-8">
        <i class="pi pi-exclamation-triangle text-4xl text-yellow-500"></i>
        <p class="text-xl mt-4">Contract not found.</p>
      </div>
    </ng-template>
  </p-card>
</div>
